<template>
  <b-card>

    <b-row>

      <!-- Название и код -->
      <b-col cols="12" xl="4" class="d-flex justify-content-between flex-column">
        <div class="d-flex justify-content-start">
          <b-avatar
            :src="stationData.avatar"
            :text="avatarText(stationData.name)"
            variant="light-primary"
            size="104px"
            rounded
          />
          <div class="d-flex flex-column ml-1">
            <div class="mb-1">
              <p class="text-muted mb-0">код ЕСР {{ stationData.number }}</p>
              <h4 class="mb-0">{{ stationData.name }}</h4>
              <small v-if="stationData.name_eng" class="text-muted">{{ stationData.name_eng }}</small>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Реквизиты справочника -->
      <b-col cols="12" xl="5">
        <table class="mt-2 mt-xl-0 w-100 station-props">
          <tr v-if="stationData.uic_code">
            <th>Код АСУ Экспресс (УИК)</th>
            <td>{{ stationData.uic_code }}</td>
          </tr>
          <tr v-if="stationData.name_alt">
            <th>Альтернативное название</th>
            <td>{{ stationData.name_alt }}</td>
          </tr>
          <tr v-if="stationData.network_mark">
            <th>Сетевая разметка ЖД</th>
            <td>{{ stationData.network_mark }}</td>
          </tr>
          <tr v-if="railway">
            <th>Дорога</th>
            <td>
              <b-link :to="{ name: 'railway_structure-railway-view', params: { id: railway.id } }">
                {{ railway.name }}
              </b-link>
              <span v-if="railway.short_name" class="text-muted"> ({{ railway.short_name }})</span>
              <span v-if="railway.number" class="text-muted">, код {{ railway.number }}</span>
            </td>
          </tr>
          <tr v-if="sector">
            <th>Участок</th>
            <td>
              <b-link :to="{ name: 'railway_structure-sector-view', params: { id: sector.id } }">
                {{ sector.name }}
              </b-link>
              <span v-if="sector.number" class="text-muted"> ({{ sector.number }})</span>
            </td>
          </tr>
          <tr v-if="stationData.point_kind">
            <th>Тип пункта</th>
            <td>{{ stationData.point_kind }}</td>
          </tr>
          <tr v-if="stationData.station_kind">
            <th>Тип станции</th>
            <td>{{ stationData.station_kind }}</td>
          </tr>
          <tr v-if="stationData.is_transit !== null">
            <th>Транзитный пункт</th>
            <td>{{ yesNo(stationData.is_transit) }}</td>
          </tr>
          <tr v-if="stationData.is_freight !== null">
            <th>Грузовые работы</th>
            <td>{{ yesNo(stationData.is_freight) }}</td>
          </tr>
          <tr v-if="stationData.lines_count !== null">
            <th>Соединяет участков</th>
            <td>{{ stationData.lines_count }}</td>
          </tr>
          <tr v-if="stationData.is_active !== null">
            <th>Действующая</th>
            <td>{{ yesNo(stationData.is_active) }}</td>
          </tr>
          <tr v-if="stationData.in_tariff_guide">
            <th>Тарифное руководство 4</th>
            <td>есть</td>
          </tr>
          <tr v-if="stationData.region || stationData.country">
            <th>Расположение</th>
            <td>{{ [stationData.country, stationData.region].filter(Boolean).join(', ') }}</td>
          </tr>
          <tr v-if="coords">
            <th>Координаты</th>
            <td>{{ coords[0].toFixed(6) }}°, {{ coords[1].toFixed(6) }}°</td>
          </tr>
        </table>
      </b-col>

      <!-- Связанные объекты: счётчик открывает вкладку со списком -->
      <b-col cols="12" xl="3">
        <table class="mt-2 mt-xl-0 w-100">
          <tr class="cursor-pointer" @click="$emit('open-tab', 'platform')" title="Перейти к списку">
            <th class="pb-50">
              <feather-icon icon="LayersIcon" class="mr-75" />
              <span class="font-weight-bold text-primary">Платформ</span>
            </th>
            <td class="pb-50">{{ stationData.platform_count }}</td>
          </tr>
          <tr class="cursor-pointer" @click="$emit('open-tab', 'pillar')" title="Перейти к списку">
            <th class="pb-50">
              <feather-icon icon="ColumnsIcon" class="mr-75" />
              <span class="font-weight-bold text-primary">Опор</span>
            </th>
            <td class="pb-50">{{ stationData.opora_count }}</td>
          </tr>
          <tr class="cursor-pointer" @click="$emit('open-tab', 'bridge')" title="Перейти к списку">
            <th class="pb-50">
              <feather-icon icon="GitCommitIcon" class="mr-75" />
              <span class="font-weight-bold text-primary">Мостов</span>
            </th>
            <td class="pb-50">{{ stationData.most_bridge_count }}</td>
          </tr>
          <tr class="cursor-pointer" @click="$emit('open-tab', 'shed')" title="Перейти к списку">
            <th class="pb-50">
              <feather-icon icon="UmbrellaIcon" class="mr-75" />
              <span class="font-weight-bold text-primary">Навесов</span>
            </th>
            <td class="pb-50">{{ stationData.naves_count }}</td>
          </tr>
          <tr class="cursor-pointer" @click="$emit('open-tab', 'fencing')" title="Перейти к списку">
            <th class="pb-50">
              <feather-icon icon="SquareIcon" class="mr-75" />
              <span class="font-weight-bold text-primary">Торцевых ограждений</span>
            </th>
            <td class="pb-50">{{ stationData.torcevoe_ograzdenie_count }}</td>
          </tr>
        </table>
      </b-col>
    </b-row>

    <!-- Станция на карте -->
    <b-row v-if="coords" class="mt-2">
      <b-col cols="12">
        <h5 class="mb-1">Станция {{ stationData.name }} на карте</h5>
        <l-map
          :zoom="13"
          :center="coords"
          style="height: 320px; width: 100%; z-index: 0"
        >
          <l-tile-layer :url="tileUrl" />
          <l-marker :lat-lng="coords">
            <l-tooltip>{{ stationData.name }} — код ЕСР {{ stationData.number }}</l-tooltip>
          </l-marker>
        </l-map>
      </b-col>
    </b-row>

  </b-card>
</template>

<script>
import {
  BCard, BAvatar, BRow, BCol, BLink,
} from 'bootstrap-vue'
import {
  LMap, LTileLayer, LMarker, LTooltip,
} from 'vue2-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import get from 'lodash.get'
import { avatarText } from '@core/utils/filter'

// Без этого маркер не находит свои картинки после сборки
// eslint-disable-next-line no-underscore-dangle
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

export default {
  components: {
    BCard, BRow, BCol, BAvatar, BLink, LMap, LTileLayer, LMarker, LTooltip,
  },
  props: {
    stationData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    sector() {
      return get(this.stationData, 'railway_sector') || null
    },
    railway() {
      return get(this.stationData, 'railway_sector.railway') || null
    },
    coords() {
      const lat = get(this.stationData, 'geolocation.point.lat')
      const lon = get(this.stationData, 'geolocation.point.long')
      return lat && lon ? [lat, lon] : null
    },
  },
  setup() {
    const yesNo = value => (value ? 'да' : 'нет')
    return {
      avatarText,
      get,
      yesNo,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    }
  },
}
</script>

<style scoped>
.station-props th {
  font-weight: 500;
  color: #6e6b7b;
  padding-right: 1rem;
  white-space: nowrap;
  vertical-align: top;
}
.station-props td {
  padding-bottom: 0.35rem;
}
</style>
